import { useCallback, useMemo } from 'react';

import { useBroadcastScheduleProgram } from '../context/BroadcastScheduleProgramContext';

type TCommonItem = {
  id: string;
};

type TNavigationList<T extends TCommonItem> = {
  items: T[];
  activeItem: T | null;
  setActiveItem: React.Dispatch<React.SetStateAction<T | null>>;
};

// Websocket presence follows the active item on its own (see `usePresenceLease`),
// so navigating only has to move the selection.
export function useNavigationList<T extends TCommonItem>({
  items,
  activeItem,
  setActiveItem,
}: TNavigationList<T>) {
  const { setVisibleBox } = useBroadcastScheduleProgram();

  const handleGoToNext = useCallback(() => {
    setActiveItem(prev => {
      if (!prev) return prev;
      const currentIndex = items.findIndex(item => item.id === prev.id);
      if (currentIndex === -1) return prev;
      const nextIndex = (currentIndex + 1) % items.length;
      setVisibleBox(null);
      return items[nextIndex];
    });
  }, [items, setVisibleBox]);

  const handleGoToPrevious = useCallback(() => {
    setActiveItem(prev => {
      if (!prev) return prev;
      const currentIndex = items.findIndex(item => item.id === prev.id);
      if (currentIndex === -1) return prev;
      const previousIndex = (currentIndex - 1 + items.length) % items.length;
      setVisibleBox(null);
      return items[previousIndex];
    });
  }, [items, setVisibleBox]);

  const handleGoToFirst = useCallback(() => {
    setActiveItem(prev => {
      if (!prev) return prev;
      setVisibleBox(null);
      return items[0];
    });
  }, [items, setVisibleBox]);

  const handleGoToLast = useCallback(() => {
    setActiveItem(prev => {
      if (!prev) return prev;
      setVisibleBox(null);
      return items[items.length - 1];
    });
  }, [items, setVisibleBox]);

  const hasNextItem = useMemo(() => {
    if (!activeItem) return null;
    const currentIndex = items.findIndex(item => item.id === activeItem.id);
    return currentIndex < items.length - 1;
  }, [activeItem, items]);

  const hasPreviousItem = useMemo(() => {
    if (!activeItem) return null;
    const currentIndex = items.findIndex(item => item.id === activeItem.id);
    return currentIndex > 0;
  }, [activeItem, items]);

  return {
    handleGoToNext,
    handleGoToPrevious,
    handleGoToFirst,
    handleGoToLast,
    hasNextItem,
    hasPreviousItem,
  };
}
